import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

export interface MediaLibraryItem {
  id: string;
  brand_id: string;
  user_id: string | null;
  name: string;
  url: string;
  alt_text: string | null;
  file_size: number | null;
  mime_type: string | null;
  created_at: string;
  updated_at: string;
}

export const useMediaLibrary = () => {
  const { currentBrandId: brandId } = useBrandManager();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: media = [], isLoading, error } = useQuery({
    queryKey: ['media-library', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      
      const { data, error } = await supabase
        .from('os_media_library')
        .select('*')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as MediaLibraryItem[];
    },
    enabled: !!brandId,
  });

  const uploadMedia = useMutation({
    mutationFn: async ({ 
      file, 
      altText 
    }: { 
      file: File; 
      altText?: string;
    }) => {
      if (!brandId) throw new Error('Brand ID is required');

      // Upload to storage bucket
      const fileName = `${brandId}/${Date.now()}_${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('article-media')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('article-media')
        .getPublicUrl(fileName);

      // Create record in os_media_library
      const { data, error } = await supabase
        .from('os_media_library')
        .insert({
          brand_id: brandId,
          user_id: user?.id || null,
          name: file.name,
          url: urlData.publicUrl,
          alt_text: altText || null,
          file_size: file.size,
          mime_type: file.type,
        })
        .select()
        .single();

      if (error) throw error;
      return data as MediaLibraryItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-library', brandId] });
      toast({
        title: 'Media uploaded',
        description: 'Your file has been uploaded successfully.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Upload failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateMedia = useMutation({
    mutationFn: async ({ 
      id, 
      altText,
      name 
    }: { 
      id: string;
      altText?: string;
      name?: string;
    }) => {
      const updateData: Record<string, string> = {};
      if (altText !== undefined) updateData.alt_text = altText;
      if (name !== undefined) updateData.name = name;

      const { data, error } = await supabase
        .from('os_media_library')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as MediaLibraryItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-library', brandId] });
      toast({
        title: 'Media updated',
        description: 'Metadata has been saved.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Update failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const saveFromUrl = useMutation({
    mutationFn: async ({ 
      sourceUrl, 
      altText,
      name 
    }: { 
      sourceUrl: string;
      altText: string;
      name?: string;
    }) => {
      if (!brandId) throw new Error('Brand ID is required');

      // Fetch the image as blob
      const response = await fetch(sourceUrl);
      if (!response.ok) throw new Error('Failed to fetch image');
      const blob = await response.blob();

      // Generate filename
      const extension = blob.type.split('/')[1] || 'jpg';
      const fileName = `${brandId}/${Date.now()}_imported.${extension}`;

      // Upload to storage bucket
      const { error: uploadError } = await supabase.storage
        .from('article-media')
        .upload(fileName, blob);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('article-media')
        .getPublicUrl(fileName);

      // Create record in os_media_library
      const { data, error } = await supabase
        .from('os_media_library')
        .insert({
          brand_id: brandId,
          user_id: user?.id || null,
          name: name || 'Imported Image',
          url: urlData.publicUrl,
          alt_text: altText || null,
          file_size: blob.size,
          mime_type: blob.type,
        })
        .select()
        .single();

      if (error) throw error;
      return data as MediaLibraryItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-library', brandId] });
      toast({
        title: 'Image saved to library',
        description: 'The image has been imported with metadata.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Import failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteMedia = useMutation({
    mutationFn: async (mediaId: string) => {
      const { error } = await supabase
        .from('os_media_library')
        .delete()
        .eq('id', mediaId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-library', brandId] });
      toast({
        title: 'Media deleted',
        description: 'The file has been removed from your library.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Delete failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    media,
    isLoading,
    error,
    uploadMedia,
    updateMedia,
    saveFromUrl,
    deleteMedia,
  };
};
